import React, { useState, useEffect } from 'react';
import superagent from 'superagent';
import { Card, CardContent } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';
import { 
  Dialog,
  DialogContent 
} from '@shadcn/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shadcn/components/ui/tooltip';
import { Eye, CheckCheck, X, Settings } from 'lucide-react';

import moment from 'moment';
import { backendUrl } from '@helpers/consts';
import useQuery from '@helpers/JIFI/useQuery';
import AlertConfigModal from './AlertConfigModal';

const AlertDashboard = () => {
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedAction, setSelectedAction] = useState(''); // 'acknowledged', 'resolved', 'dismissed'
  const [actionNotes, setActionNotes] = useState('');
  const [showActionModal, setShowActionModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch alerts với filters
  const [alertsData, alertsState] = useQuery({
    url: '/inventory/alerts',
    queryData: {
      severity: selectedSeverity || undefined,
      type: selectedType || undefined, 
      locationId: selectedLocation || undefined,
      status: showResolved ? 'resolved' : undefined,
      page,
      limit: 50
    },
    refresh: refreshTrigger
  });

  
  const [statsData, statsState] = useQuery({
    url: '/inventory/alerts/statistics',
    refresh: refreshTrigger
  });

  
  const [locationsData, locationsState] = useQuery({
    url: '/inventory/getAllLocationNames'
  });

  const alerts = alertsData?.alerts || [];
  const totalPages = alertsData?.totalPages || 1;
  
  const stats = statsData || {};
  const locations = locationsData || [];

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return '🚨';
      case 'high': return '⚠️';
      case 'medium': return '📋';
      case 'low': return 'ℹ️';
      default: return '📢';
    }
  };

  const getAlertTypeDisplay = (type) => {
    switch (type) {
      case 'accuracy_decline':
      case 'accuracy_degradation':
        return 'Accuracy Decline';
      case 'high_variance':
        return 'High Variance';
      case 'tolerance_breach':
        return 'Tolerance Breach';
      default:
        return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
    }
  };

  const formatAlertValue = (alert) => {
    const currentValue = alert.metrics?.currentValue;
    

    if (currentValue === null || currentValue === undefined) return 'N/A';

    switch (alert.type) {
      case 'high_variance':
        return `$${currentValue.toFixed(1)}`;
      case 'tolerance_breach':
        return `${(currentValue * 100).toFixed(1)}%`;
      case 'accuracy_decline':
      case 'accuracy_degradation':
      default:
        return `${currentValue.toFixed(1)}%`;
    }
  };

  const formatThresholdValue = (alert) => {
    const thresholdValue = alert.metrics?.thresholdValue;

    if (thresholdValue === null || thresholdValue === undefined) return 'N/A';

    switch (alert.type) {
      case 'high_variance':
        return `$${thresholdValue.toFixed(1)}`;
      case 'tolerance_breach':
        return `${(thresholdValue * 100).toFixed(1)}%`;
      case 'accuracy_decline':
      case 'accuracy_degradation':
      default:
        return `${thresholdValue.toFixed(1)}%`;
    }
  };

  const handleStatusAction = async (alert, action) => {
    setSelectedAlert(alert);
    setSelectedAction(action);
    setActionNotes('');
    setShowActionModal(true);
  };

  const submitStatusUpdate = async () => {
    try {
      const payload = {
        status: selectedAction,
        updatedBy: localStorage.getItem('userId') || 'system',
        notes: actionNotes || undefined
      };

      

      await superagent
        .put(`${backendUrl}/inventory/alerts/${selectedAlert.id}/status`)
        .send(payload)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`);
      
      setShowActionModal(false);
      setSelectedAlert(null);
      setSelectedAction('');
      setActionNotes('');
      
      // Refresh data
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error(`Error updating alert status to ${selectedAction}:`, error);
      alert(`Failed to ${selectedAction} alert`);
    }
  };

 

  const handleClearFilters = () => {
    setSelectedSeverity('');
    setSelectedType('');
    setSelectedLocation('');
    setShowResolved(false);
    setPage(1);
  };
  
  
  useEffect(() => {
    setPage(1);
  }, [selectedSeverity, selectedType, selectedLocation, showResolved]);

  if (alertsState === 'loading' || statsState === 'loading' || locationsState === 'loading') {
    return (
      <div className="flex flex-col justify-center items-center py-16">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600 font-medium">Loading alerts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Settings Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Alert Management</h2>
          <p className="text-sm text-gray-600">Monitor and configure inventory accuracy alerts</p>
        </div>
        <Button
          onClick={() => setShowConfigModal(true)}
          variant="outline"
          className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white rounded-lg font-medium"
        >
          <Settings className="h-4 w-4 mr-2" />
          Alert Settings
        </Button>
      </div>

      {/* Alert Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Critical Alerts</p>
                <p className="text-2xl font-bold text-red-700">{stats.bySeverity?.critical || stats.criticalAlerts || 0}</p>
              </div>
              <div className="text-2xl">🚨</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">High Priority</p>
                <p className="text-2xl font-bold text-orange-700">{stats.bySeverity?.high || 0}</p>
              </div>
              <div className="text-2xl">⚠️</div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Alerts</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.byStatus?.active || stats.activeAlerts || 0}</p>
              </div>
              <div className="text-2xl">📋</div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Alerts</p>
                <p className="text-2xl font-bold text-blue-700">{stats.total || 0}</p>
              </div>
              <div className="text-2xl">📊</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Type Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-red-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Accuracy Issues</p>
                <p className="text-xl font-bold text-red-600">{stats.byType?.accuracy_degradation || 0}</p>
                <p className="text-xs text-gray-500">Accuracy Degradation</p>
              </div>
              <div className="text-xl">📉</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border border-yellow-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Tolerance Issues</p>
                <p className="text-xl font-bold text-yellow-600">{stats.byType?.tolerance_breach || 0}</p>
                <p className="text-xs text-gray-500">Tolerance Breach</p>
              </div>
              <div className="text-xl">⚖️</div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Variance Issues</p>
                <p className="text-xl font-bold text-purple-600">{stats.byType?.high_variance || 0}</p>
                <p className="text-xs text-gray-500">High Variance</p>
              </div>
              <div className="text-xl">📊</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Status Breakdown */}
      {stats.byStatus && (
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-4">Alert Status Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.byStatus.active || 0}</p>
                <p className="text-sm text-gray-600">Active</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.byStatus.acknowledged || 0}</p>
                <p className="text-sm text-gray-600">Acknowledged</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.byStatus.resolved || 0}</p>
                <p className="text-sm text-gray-600">Resolved</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-600">{stats.byStatus.dismissed || 0}</p>
                <p className="text-sm text-gray-600">Dismissed</p>
              </div>
             
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md bg-white"
              >
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md bg-white"
              >
                <option value="">All Types</option>

                <option value="accuracy_degradation">Accuracy Degradation</option>
                <option value="high_variance">High Variance</option>
                <option value="tolerance_breach">Tolerance Breach</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md bg-white"
              >
                <option value="">All Locations</option>
                {locations.map((location) => (
                  <option key={location._id} value={location._id}>
                    {location.display_name || location.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showResolved}
                  onChange={(e) => setShowResolved(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Show Resolved</span>
              </label>
            </div>

            <div>
              <Button 
                onClick={handleClearFilters}
                variant="outline"
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Alert</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Location</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Details</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Description</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {alerts.map((alert) => (
                  <tr key={alert._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">{getSeverityIcon(alert.severity)}</span>
                        <div>
                          <div className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(alert.severity)}`}>
                            {alert.severity?.toUpperCase()}
                          </div>
                          <p className="text-sm font-medium text-gray-900 mt-1">
                            {getAlertTypeDisplay(alert.type)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{alert.location?.name || 'Unknown Location'}</p>
                      <p className="text-sm text-gray-500">Code: {alert.location?.code || 'N/A'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900">
                        Current: {formatAlertValue(alert)}
                      </p>
                      <p className="text-sm text-gray-500">
                        Threshold: {formatThresholdValue(alert)}
                      </p>
                      <p className="text-sm text-gray-500">
                        Affected: {alert.metrics?.affectedItems || 0} items
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700 max-w-xs">
                        {alert.description || alert.message || 'No description available'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900">
                        {moment(alert.createdAt).format('DD/MM/YYYY')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {moment(alert.createdAt).format('HH:mm')}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <TooltipProvider>
                        <div className="flex items-center justify-center space-x-2">
                          {alert.status === 'active' && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    onClick={() => handleStatusAction(alert, 'acknowledged')}
                                    className="h-8 w-8 p-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Acknowledge Alert</p>
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    onClick={() => handleStatusAction(alert, 'resolved')}
                                    className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700 text-white rounded-full"
                                  >
                                    <CheckCheck className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Resolve Alert</p>
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    onClick={() => handleStatusAction(alert, 'dismissed')}
                                    className="h-8 w-8 p-0 bg-gray-600 hover:bg-gray-700 text-white rounded-full"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Dismiss Alert</p>
                                </TooltipContent>
                              </Tooltip>
                            </>
                          )}

                          {alert.status === 'acknowledged' && (
                            <div className="flex flex-col items-center space-y-2">
                              <span className="text-xs text-blue-600 font-medium">
                                Acknowledged
                              </span>
                              <div className="flex space-x-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      onClick={() => handleStatusAction(alert, 'resolved')}
                                      className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700 text-white rounded-full"
                                    >
                                      <CheckCheck className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Resolve Alert</p>
                                  </TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      onClick={() => handleStatusAction(alert, 'dismissed')}
                                      className="h-8 w-8 p-0 bg-gray-600 hover:bg-gray-700 text-white rounded-full"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Dismiss Alert</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </div>
                          )}

                          {alert.status === 'resolved' && (
                            <span className="text-xs text-green-600 font-medium">
                              ✅ Resolved
                            </span>
                          )}

                          {alert.status === 'dismissed' && (
                            <span className="text-xs text-gray-600 font-medium">
                              🚫 Dismissed
                            </span>
                          )}
                        </div>
                      </TooltipProvider>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {alerts.length === 0 && (
            <div className="text-center py-12">
              
              <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts found</h3>
              <p className="text-gray-500">All systems running smoothly!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2">
          <Button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            variant="outline"
          >
            Previous
          </Button>
          <span className="flex items-center px-4 py-2 text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <Button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}

      
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="max-w-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              {selectedAction === 'acknowledged' && 'Acknowledge Alert'}
              {selectedAction === 'resolved' && 'Resolve Alert'} 
              {selectedAction === 'dismissed' && 'Dismiss Alert'}
            </h3>
            
            <div className="space-y-4">
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional):
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  placeholder={`Add notes about ${selectedAction}ing this alert...`}
                  className="w-full p-3 border border-gray-300 rounded-md resize-none"
                  rows={3}
                />
              </div>

              
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => setShowActionModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={submitStatusUpdate}
                className={
                  selectedAction === 'acknowledged' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                  selectedAction === 'resolved' ? 'bg-green-600 hover:bg-green-700 text-white' :
                  'bg-gray-600 hover:bg-gray-700 text-white'
                }
              >
                {selectedAction === 'acknowledged' && 'Acknowledge'}
                {selectedAction === 'resolved' && 'Resolve'}
                {selectedAction === 'dismissed' && 'Dismiss'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alert Config Modal */}
      <AlertConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onConfigActivated={() => setRefreshTrigger(prev => prev + 1)}
      />
    </div>
  );
};

export default AlertDashboard;
